import { FaceLivenessDetector } from "@aws-amplify/ui-react-liveness";
import {face_liveness_dictionary} from "@/utils/constants.ts";
import { useTranslation } from "react-i18next";
import { isMobile } from 'react-device-detect';
import st from "@/styles/Ui.module.scss"
import clsx from "clsx";
import {useEffect, useRef, useState} from "react";
import {Amplify} from "aws-amplify";
import awsmobile from "@/aws-exports.ts";
import useAuth from "@/hooks/auth.ts";
import eventEmitter from "@/utils/event_emmiter_instance.ts";
import CommonClass from "@/utils/common.ts";
import LoginLoader from "@/ui/LoginLoader.tsx";
import WarningCircleIcon from "@/svg/WarningCircleIcon.tsx";
import Button from "@/ui/Button.tsx";
import {Navigate, useNavigate} from "react-router-dom";
import DashboardClass from "@/utils/dashboard_class.ts";
import CloseButton from "@/ui/CloseButton.tsx";
import StatusMessage from "@/ui/StatusMessage.tsx";
import {useAtom} from "jotai/index";
import {profile} from "@/atoms/profile.ts";
import * as Sentry from "@sentry/react";
import {Clock} from "lucide-react";



const FaceLivenessCheck = () => {
    const {t, i18n } = useTranslation();
    const effectRan = useRef<boolean>(false)
    const auth = useAuth()
    const navigation = useNavigate()
    const [loading, set_loading] = useState<boolean>(false)
    const [transaction_id, set_transaction_id] = useState<string | undefined>(undefined);
    const [status, set_status] = useState<"success" | "in_process" | "error" | "fatale_error" | "not_passed" | "server_time_not_ok">("in_process")
    const [user] = useAtom(profile)
    const interval = useRef<any>(null)
    const is_phone = Boolean(typeof user?.phone === "string" && user.phone.length)
    const queryParams = new URLSearchParams(location.search);
    const debug = queryParams.get("debug")

    const loading_handle = (bol:boolean) => {
        set_loading(bol)
    }

    const get_transaction_id_handle = async() => {

        const id = await auth.startFaceLiveness()

        if(typeof id === "string"){

            let timeout = setTimeout(() => {
                if(debug === "true") console.info("setTimeout get_transaction_id_handle")
                set_transaction_id(id)
                clearTimeout(timeout)
                set_status("in_process")

            }, 200)

        } else {
            set_status("error")
            Sentry.captureException(new Error("Error getting transaction ID for face liveness"));
        }
    }

    const set_server_time_err_handler = () => {
        set_status("server_time_not_ok")
    }

    useEffect(() => {
        Amplify.configure(awsmobile);

        if(!effectRan.current){
            eventEmitter.on(CommonClass.CustomEvents.set_req_load, loading_handle)
            eventEmitter.on(CommonClass.CustomEvents.server_time_not_good, set_server_time_err_handler)
            get_transaction_id_handle()
        }

        return () => {
            if(effectRan.current) {
                eventEmitter.off(CommonClass.CustomEvents.set_req_load, loading_handle)
                eventEmitter.off(CommonClass.CustomEvents.server_time_not_good, set_server_time_err_handler)
            }
            clearInterval(interval.current)
            effectRan.current = true
        }

    }, []);

    const component = () => {
        switch (status) {
            case "in_process":
                return transaction_id && (
                    <FaceLivenessDetector
                        sessionId={transaction_id}
                        region="us-east-1"
                        onAnalysisComplete={async () => {
                            const res = await auth.finishFaceLiveness(transaction_id)
                            let count = 0
                            if(res) {

                                interval.current = setInterval(async () => {

                                    count += 1

                                    if(count > 7) {
                                        clearInterval(interval.current);
                                        eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "info", t("kyc_will_continue"))
                                        set_status("not_passed")
                                    }

                                    if(debug === "true") console.info("FaceLivenessDetector interval small_update 2")

                                    try {
                                        const updated = await auth.small_update();

                                        const status = updated?.kyc?.liveness;
                                        const comparison_status = updated?.kyc?.face_comparison
                                        const gen_status = updated?.kyc_general_status

                                        if(gen_status === "ProcessingSuccess"){
                                            set_status("success")
                                            clearInterval(interval.current);

                                        } else if(status === "ProcessingFailed" || comparison_status === "ProcessingFailed") {
                                            set_status("error")
                                            clearInterval(interval.current);

                                        } else if(status === "ProcessingFailedAndSentToManager" || comparison_status === "ProcessingFailedAndSentToManager") {
                                            set_status("fatale_error")
                                            clearInterval(interval.current);

                                        }

                                    } catch (error) {
                                        console.error("Error updating:", error);
                                        Sentry.captureException(error);
                                        set_status("error")
                                        clearInterval(interval.current);
                                    }

                                }, 2000);

                            } else {
                                set_status("error")
                                Sentry.captureException(new Error("Didn't get any response from finishFaceLiveness API"));
                            }

                        }}

                        onUserCancel={() => {set_status("error")}}

                        onError={(error) => {
                            let message: string = "";

                            switch (error.state) {
                                case "FACE_DISTANCE_ERROR":
                                    message = t("FACE_DISTANCE_ERROR");
                                    break;
                                case "MULTIPLE_FACES_ERROR":
                                    message = t("MULTIPLE_FACES_ERROR");
                                    break;
                                case "MOBILE_LANDSCAPE_ERROR":
                                    message = t("MOBILE_LANDSCAPE_ERROR");
                                    break;
                                case "TIMEOUT":
                                    message = t("LIVENESS_TIMEOUT");
                                    break;
                                case "CAMERA_FRAMERATE_ERROR":
                                    message = t("LIVENESS_TIMEOUT");
                                    break;
                                case "CAMERA_ACCESS_ERROR":
                                    message = t("CAMERA_ACCESS_ERROR");
                                    break;
                                case "RUNTIME_ERROR":
                                    message = t("RUNTIME_ERROR");
                                    break;
                                case "CONNECTION_TIMEOUT":
                                    message = t("CONNECTION_TIMEOUT");
                                    break;
                                case "FRESHNESS_TIMEOUT":
                                    message = t("FRESHNESS_TIMEOUT");
                                    break;
                                case "SERVER_ERROR":
                                    message = t("SERVER_ERROR");
                                    break;
                                default:
                                    message = t("RUNTIME_ERROR");
                                    break

                            }
                            console.error(error)

                            Sentry.captureException(error, {
                                tags: { section: "face liveness component errors" },
                            });

                            if(message.length > 0){
                                eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", message);

                            } else {
                                eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t("error_message"));
                                eventEmitter.emit(CommonClass.CustomEvents.no_connection_listener_show, true)

                            }
                        }}

                        displayText={
                            // @ts-ignore
                            face_liveness_dictionary[i18n.language]
                        }
                    />
                )

            case "success":
                return <Navigate to={DashboardClass.dashboard_routes.SuccessKYC} />

            case "not_passed":
                return <Navigate to={DashboardClass.dashboard_routes.Home} />

            case "error":
                return (
                    <div className={st.err_screen}>
                        <WarningCircleIcon  />

                        {t("error_message_try_once_more")}

                        <Button
                            onClick={() => {
                                get_transaction_id_handle()
                            }}
                        >
                            {t("reset")}
                        </Button>
                    </div>
                )

            case "server_time_not_ok":
                return (
                    <div className={clsx(st.err_screen, st.server_time_err)}>
                        <Clock />

                        <span>
                            {t("server_time_not_good")}
                        </span>

                        <Button
                            onClick={() => {
                                get_transaction_id_handle()
                            }}
                        >
                            {t("reset")}
                        </Button>
                    </div>
                )

            case "fatale_error":
                return <StatusMessage
                    is_phone={is_phone}
                    status={"danger"}
                    text={t("failed_kyc1")}
                />

        }
    }


    return (
        <div className={clsx(st.aws_face_liveness, isMobile && st.mobile)}>
            {isMobile && (
                <CloseButton
                    className={st.close_btn}
                    onClick={() => {
                        navigation(DashboardClass.dashboard_routes.Home)
                    }}
                />
            )}

            {component()}

            {loading && <LoginLoader />}

        </div>
    );
};





export default FaceLivenessCheck;
